// The public BaZi calculator's response (POST /v2/bazi/calculator). Field names are those of the original
// discovery-dev calculator, so this page reads the result the way that page did.

export interface TenGod {
  name: string;
  nameEn: string;
}

export interface HiddenStem {
  id: number;
  name: string;
  element: string;
  weight: number;
  order: number;
  tenGod: TenGod;
}

export interface Relation {
  name: string;
  hs1?: string;
  hs2?: string;
  eb1?: string;
  eb2?: string;
  eb3?: string;
  element?: string;
}

export interface YinRelation {
  name: string;
  a: string;
  b: string;
}

export interface SenShaEntry {
  name: string;
  value: string;
}

export interface Pillar {
  heavenlyStem: { id: number; name: string; type: string; element: string; weight: number; tenGod: TenGod };
  earthlyBranch: { id: number; name: string; type: string; element: string; hiddenStems: HiddenStem[] };
  growStage: { id: number; name: string };
  naYin: string;
  yiJings: Array<{ id: number; name: string }>;
  hsCombinations: Relation[];
  hsClashes: Relation[];
  ebCombinations: Relation[];
  ebThreeCombinations: Relation[];
  ebHalfCombination: Relation[];
  ebDirCombinations: Relation[];
  ebClashes: Relation[];
  ebBullyingPunishments: Relation[];
  ebSelfPunishments: Relation[];
  ebUngratefulPunishments: Relation[];
  ebUncivilizedPunishments: Relation[];
  ebHarms: Relation[];
  ebDestructions: Relation[];
  fuYins: YinRelation[];
  fanYins: YinRelation[];
  senSha: {
    dayStemBased: SenShaEntry[];
    dayBranchBased: SenShaEntry[];
    monthBranchBased: SenShaEntry[];
    yearBranchBased: SenShaEntry[];
  };
}

export interface Contribution {
  hs: number;
  eb: number;
}

export interface FiveElement {
  hourPillar: Contribution;
  dayPillar: Contribution;
  monthPillar: Contribution;
  yearPillar: Contribution;
  composition: number;
  seasonWeight: number;
  predominant: number;
  dmElement: string;
  altName: string;
  compositionPercentage: number;
  predominantPercentage: number;
}

export const ELEMENTS = ["wood", "fire", "earth", "metal", "water"] as const;
export type ElementName = (typeof ELEMENTS)[number];

export type FiveStructures = Record<ElementName, FiveElement> & {
  totalComposition: number;
  totalPredominant: number;
  chartData: number[];
};

export interface ChartData {
  labels: string[];
  data: number[];
}

export interface TenGodStem {
  tenGod: string;
  hourPillar: Contribution;
  dayPillar: Contribution;
  monthPillar: Contribution;
  yearPillar: Contribution;
  composition: number;
  compositionPercentage: number;
  seasonWeight: number;
  predominant: number;
  predominantPercentage: number;
  basic: number;
}

export type TenGodStructures = Record<string, TenGodStem> & {
  totalComposition: number;
  totalPredominant: number;
  totalBasic: number;
  chartData: ChartData;
  highest: string | null;
};

export interface TenGodTotalStem {
  tenGod: string;
  hourBasicTotal: number;
  dayBasicTotal: number;
  monthBasicTotal: number;
  yearBasicTotal: number;
  composition: number;
  compositionPercentage: number;
  seasonWeight: number;
  predominant: number;
  predominantPercentage: number;
}

export type TenGodTotals = Record<string, TenGodTotalStem> & {
  totalComposition: number;
  totalPredominant: number;
  chartData: ChartData;
};

export const STEM_NAMES = ["jia", "yi", "bing", "ding", "wu", "ji", "geng", "xin", "ren", "gui"] as const;

export interface BaziChart {
  yearPillar: Pillar;
  monthPillar: Pillar;
  dayPillar: Pillar;
  hourPillar: Pillar;
  taiYuanPillar: Pillar;
  mingGongPillar: Pillar;
  luckPillar: Pillar;
  annualPillar: Pillar;
  fiveStructures: FiveStructures;
  yearTenGodStructures: TenGodStructures;
  monthTenGodStructures: TenGodStructures;
  dayTenGodStructures: TenGodStructures;
  hourTenGodStructures: TenGodStructures;
  tenGodStructures: TenGodTotals;
  zodiac: string;
}

export interface BaziInput {
  year: number;
  month: number;
  day: number;
  hour: number;
  gender: 1 | 2;
}
